import { checkDispenseRecord, checkDocument } from '../fhir/check';
import { dispenseMatchesPrescription } from '../fhir/dispensing';
import { readDispenseRecord, readPrescription } from '../fhir/read';
import { documentDateOf, patientAccountId } from '../fhir/summary';
import type { Bundle } from '../fhir/types';
import { canAccess, canWrite, type Role } from '../roles';
import type { PracticeErrorCode, PracticeRequest, PracticeResponse, PracticeResult, RecordWrite } from './protocol';
import { MAX_RECORD_BYTES, categoryOfDocument, type HeldRecord, type RecordEntry, type RecordStore } from './records';

/** A member as the record holder knows them: their account and their roles in this practice. */
export interface HolderMember {
  accountId: string;
  roles: readonly Role[];
}

/** Most bytes of records one answer carries, so it fits one envelope. Larger answers come in pages. */
export const RESPONSE_RECORD_BUDGET = 192 * 1024;

const byteLength = (value: unknown) => new TextEncoder().encode(JSON.stringify(value)).length;

class Refusal extends Error {
  constructor(
    readonly code: PracticeErrorCode,
    message: string,
    readonly record?: HeldRecord,
  ) {
    super(message);
  }
}

// One write at a time per store, so a version check and its save cannot interleave.
const writeLocks = new WeakMap<RecordStore, Promise<unknown>>();

/** Runs a read-check-write task on a store with no other write in between. Members' writes and received shares both use it. */
export function withStoreLock<T>(store: RecordStore, task: () => Promise<T>): Promise<T> {
  const previous = writeLocks.get(store) ?? Promise.resolve();
  const next = previous.then(task, task);
  writeLocks.set(store, next.catch(() => undefined));
  return next;
}

async function page(store: RecordStore, entries: RecordEntry[], include: (entry: RecordEntry) => boolean, from: number) {
  const records: HeldRecord[] = [];
  let size = 0;
  let next = from;
  for (const entry of entries) {
    if (include(entry)) {
      const record = await store.get(entry.id);
      if (record) {
        const bytes = byteLength(record);
        if (records.length > 0 && size + bytes > RESPONSE_RECORD_BUDGET) return { records, next, more: true };
        records.push(record);
        size += bytes;
      }
    }
    next = Math.max(next, entry.seq);
  }
  return { records, next, more: false };
}

function patientProblem(resource: Record<string, unknown>): string | null {
  if (resource.resourceType !== 'Patient') return 'Demographics must be a FHIR Patient';
  const names = resource.name as { text?: unknown }[] | undefined;
  const name = Array.isArray(names) ? names[0]?.text : undefined;
  if (typeof name !== 'string' || name.trim().length === 0 || name.length > 100) return 'The patient needs a name of up to 100 characters';
  return null;
}

async function write(store: RecordStore, member: HolderMember, requestId: string, input: RecordWrite, now: Date): Promise<HeldRecord> {
  let category;
  if (input.kind === 'patient') {
    if (input.id !== input.patientId) throw new Refusal('invalid_request', 'A patient record has the patient’s own ID');
    if (input.hiType) throw new Refusal('invalid_request', 'A patient record has no health information type');
    const problem = patientProblem(input.resource);
    if (problem) throw new Refusal('invalid_request', problem);
    category = 'demographics' as const;
  } else if (input.kind === 'dispense') {
    if (input.hiType) throw new Refusal('invalid_request', 'A dispensing record has no health information type');
    if (!input.prescriptionId) throw new Refusal('invalid_request', 'A dispensing record names the prescription it answers');
    const [issue] = checkDispenseRecord(input.resource);
    if (issue) throw new Refusal('invalid_request', `The dispensing record is not valid: ${issue.path} ${issue.message}`.slice(0, 300));
    category = 'dispensing' as const;
  } else {
    if (!input.hiType) throw new Refusal('invalid_request', 'A document needs its health information type');
    const [issue] = checkDocument(input.resource, input.hiType);
    if (issue) throw new Refusal('invalid_request', `The document is not valid: ${issue.path} ${issue.message}`.slice(0, 300));
    category = categoryOfDocument(input.hiType);
  }
  if (!canWrite(member.roles, category)) throw new Refusal('forbidden', `Your roles in this practice do not allow writing ${category} records`);
  if (byteLength(input.resource) > MAX_RECORD_BYTES) throw new Refusal('too_large', 'The record is too large');

  const at = now.toISOString();
  // What the holder reads from the record itself: the linked account of a
  // patient, so shares find the patient, and a document's own date.
  const accountId = input.kind === 'patient' ? patientAccountId(input.resource) : undefined;
  const date = input.kind === 'patient' ? undefined : (documentDateOf(input.resource) ?? at);
  if (input.prescriptionId && input.kind !== 'dispense') throw new Refusal('invalid_request', 'Only a dispensing record names a prescription');

  return withStoreLock(store, async () => {
    if (input.kind !== 'patient') {
      const patient = await store.get(input.patientId);
      if (patient?.kind !== 'patient') throw new Refusal('not_found', 'The practice has no such patient');
    }
    if (input.kind === 'dispense') {
      // A dispensing record answers a prescription this holder keeps for the same patient, item by item.
      const prescription = input.prescriptionId ? await store.get(input.prescriptionId) : null;
      if (prescription?.kind !== 'document' || prescription.hiType !== 'Prescription' || prescription.patientId !== input.patientId) {
        throw new Refusal('not_found', 'The practice has no such prescription for this patient');
      }
      let matches = false;
      try {
        matches = dispenseMatchesPrescription(readDispenseRecord(input.resource as unknown as Bundle), readPrescription(prescription.resource as unknown as Bundle));
      } catch {
        matches = false;
      }
      if (!matches) throw new Refusal('invalid_request', 'The dispensing record lists a medicine the prescription does not');
    }
    const existing = await store.get(input.id);
    // The same request again, after its answer was lost: answer it without writing twice.
    if (existing?.requestId === requestId && existing.version === input.baseVersion + 1) return existing;
    if (existing && (existing.kind !== input.kind || existing.patientId !== input.patientId || existing.category !== category)) {
      throw new Refusal('invalid_request', 'A record cannot change its kind, patient or category');
    }
    if (existing?.source) throw new Refusal('forbidden', 'A copy shared by the patient or another practice cannot be changed');
    if ((existing?.version ?? 0) !== input.baseVersion) {
      const visible = existing && canAccess(member.roles, existing.category) ? existing : undefined;
      throw new Refusal('conflict', 'Someone saved a newer version first', visible);
    }
    return store.save({
      id: input.id,
      patientId: input.patientId,
      kind: input.kind,
      category,
      ...(input.hiType ? { hiType: input.hiType } : {}),
      version: input.baseVersion + 1,
      resource: input.resource,
      requestId,
      ...(accountId ? { accountId } : {}),
      ...(date ? { date } : {}),
      ...(input.prescriptionId ? { prescriptionId: input.prescriptionId } : {}),
      createdAt: existing?.createdAt ?? at,
      createdBy: existing?.createdBy ?? member.accountId,
      updatedAt: at,
      updatedBy: member.accountId,
    });
  });
}

async function answer(store: RecordStore, member: HolderMember, request: PracticeRequest, now: Date): Promise<PracticeResult> {
  const readable = (entry: Pick<RecordEntry, 'category'>) => canAccess(member.roles, entry.category);
  switch (request.op) {
    case 'sync': {
      if (!canAccess(member.roles, 'demographics')) throw new Refusal('forbidden', 'Your roles in this practice do not include its patients');
      const entries = await store.since(request.since);
      return { op: 'sync', ...(await page(store, entries, (entry) => entry.category === 'demographics', request.since)) };
    }
    case 'records': {
      if (!canAccess(member.roles, 'demographics')) throw new Refusal('forbidden', 'Your roles in this practice do not include its patients');
      const patient = await store.get(request.patientId);
      if (patient?.kind !== 'patient') throw new Refusal('not_found', 'The practice has no such patient');
      const entries = (await store.ofPatient(request.patientId)).filter((entry) => entry.seq > request.after);
      return { op: 'records', patientId: request.patientId, ...(await page(store, entries, readable, request.after)) };
    }
    case 'get': {
      const record = await store.get(request.id);
      if (!record) throw new Refusal('not_found', 'The practice has no such record');
      if (!readable(record)) throw new Refusal('forbidden', 'Your roles in this practice do not allow reading this record');
      return { op: 'get', record };
    }
    case 'put':
      return { op: 'put', record: await write(store, member, request.requestId, request.record, now) };
    case 'documents': {
      if (!canAccess(member.roles, categoryOfDocument(request.hiType))) {
        throw new Refusal('forbidden', 'Your roles in this practice do not allow reading these records');
      }
      const entries = await store.since(request.since);
      const wanted = (entry: RecordEntry) => entry.kind === 'document' && entry.hiType === request.hiType;
      return { op: 'documents', ...(await page(store, entries, wanted, request.since)) };
    }
    case 'register': {
      if (!canAccess(member.roles, 'dispensing')) throw new Refusal('forbidden', 'Your roles in this practice do not include its dispensing register');
      const entries = await store.since(request.since);
      return { op: 'register', ...(await page(store, entries, (entry) => entry.kind === 'dispense', request.since)) };
    }
  }
}

/**
 * Answers one request from a practice member, checking the member's roles
 * with canAccess and canWrite before reading or writing anything. The hub
 * runs it for every request that arrives through the relay, and a solo
 * doctor's device runs it directly on its own store.
 */
export async function answerPracticeRequest(
  store: RecordStore,
  member: HolderMember,
  request: PracticeRequest,
  now = new Date(),
): Promise<PracticeResponse> {
  try {
    return { v: 1, requestId: request.requestId, ok: true, result: await answer(store, member, request, now) };
  } catch (err) {
    if (!(err instanceof Refusal)) throw err;
    return {
      v: 1,
      requestId: request.requestId,
      ok: false,
      error: { code: err.code, message: err.message, ...(err.record ? { record: err.record } : {}) },
    };
  }
}
